/**
 * A proposal the assistant made: what it wants to change, and the gate.
 *
 * Nothing here has touched the timeline. Apply replays the op log onto the
 * CURRENT edit as one undoable step; Discard just marks it. Status lives in the
 * shared document, so both are visible to every collaborator.
 */
import { useMemo, useState } from 'react';
import { useAgent } from '../../state/agent';
import { useTimeline } from '../../state/timeline';
import type { ProposalRecord } from '../../crdt/agent';
import { describeOps } from '../../agent/tools/describe';
import { applyProposalOps } from '../../agent/core/loop';
import { summariseProposal, type ProposalSummary } from '../../agent/proposal/summary';
import { MUTATE_TOOLS } from '../../agent/tools/edit';

/** Explicit discriminant: `'x' in u` does not narrow a union of optional keys. */
type SummaryState =
  | { kind: 'conflict'; conflict: string }
  | { kind: 'ok'; result: ProposalSummary };

export default function ProposalCard({ record }: { record: ProposalRecord }) {
  const [expanded, setExpanded] = useState(false);
  const applyProposal = useAgent((s) => s.applyProposal);
  const discardProposal = useAgent((s) => s.discardProposal);

  const lines = describeOps(record.ops);
  const count = record.ops.length;
  const resolved = record.status !== 'pending';
  const edit = useTimeline((s) => s.edit);

  /**
   * Replay against the CURRENT edit to describe the outcome. Doing it here
   * rather than at propose time means the summary reflects the timeline as it
   * is now — and a replay that no longer applies surfaces as a conflict in the
   * summary instead of only at the moment you press Apply.
   */
  const summary = useMemo((): SummaryState | null => {
    if (resolved) return null;
    const replayed = applyProposalOps(edit, record.ops, MUTATE_TOOLS);
    if (!replayed.ok) return { kind: 'conflict', conflict: replayed.conflict };
    return { kind: 'ok', result: summariseProposal(edit, replayed.edit) };
  }, [edit, record.ops, resolved]);

  return (
    <div className={`proposal-card proposal-${record.status}`}>
      <div className="proposal-head">
        <span className="proposal-title">{record.title}</span>
        <span className="proposal-count">
          {count} operation{count === 1 ? '' : 's'}
        </span>
      </div>

      {record.status === 'conflict' && (
        <p className="proposal-conflict">
          Could not apply: {record.conflict ?? 'the timeline changed underneath it.'}
        </p>
      )}
      {record.status === 'applied' && (
        <p className="proposal-note">
          Applied{record.appliedBy ? ` by ${record.appliedBy}` : ''} — Cmd+Z undoes the whole thing.
        </p>
      )}
      {record.status === 'discarded' && <p className="proposal-note">Discarded.</p>}

      {summary?.kind === 'conflict' && (
        <p className="proposal-conflict">
          This no longer applies to the current timeline: {summary.conflict}
        </p>
      )}
      {summary?.kind === 'ok' && (
        <ul className="proposal-summary">
          {summary.result.lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      )}

      <button
        className="proposal-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? 'Hide' : 'Show'} the {count === 1 ? 'operation' : 'operations'}
      </button>
      {expanded && (
        <ol className="proposal-ops">
          {lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ol>
      )}

      {!resolved && (
        <div className="proposal-actions">
          <button
            className="primary"
            disabled={summary?.kind === 'conflict'}
            onClick={() => applyProposal(record)}
          >
            Apply
          </button>
          <button onClick={() => discardProposal(record)}>Discard</button>
        </div>
      )}
      {record.status === 'conflict' && (
        <div className="proposal-actions">
          <button onClick={() => discardProposal(record)}>Dismiss</button>
        </div>
      )}
    </div>
  );
}
